/**
 * Five folded grass blades for the field dressing.
 *
 * Each blade is a V rather than a double-sided card: two quads meet at its
 * spine at roughly 100 degrees, so a horizontal camera always finds one face
 * pointing towards it. That preserves back-face culling for roughly 1,600
 * instances while also matching the folded shape of a real grass blade.
 */
import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

export interface GrassTuftOptions {
  /** Authored per placement so one field does not repeat the default tuft. */
  readonly seed?: number;
}

const DEFAULT_SEED = 47;

function bladeGeometry(
  rootX: number,
  rootZ: number,
  azimuth: number,
  height: number,
  rootWidth: number,
  tipWidth: number,
  lean: number,
): THREE.BufferGeometry {
  const outward = new THREE.Vector3(Math.cos(azimuth), 0, Math.sin(azimuth));
  const across = new THREE.Vector3(-outward.z, 0, outward.x);
  const root = new THREE.Vector3(rootX, 0, rootZ);
  const tip = root.clone().addScaledVector(outward, Math.tan(lean) * height).setY(height);
  // The 100 degree interior fold puts each edge 40 degrees behind the spine.
  // Its normal therefore has a useful horizontal component without needing a
  // material-side override that the instancer cannot safely batch.
  const foldDepth = Math.tan(THREE.MathUtils.degToRad(40)) * rootWidth / 2;
  const rootLeft = root.clone().addScaledVector(across, -rootWidth / 2).addScaledVector(outward, -foldDepth);
  const rootRight = root.clone().addScaledVector(across, rootWidth / 2).addScaledVector(outward, -foldDepth);
  const tipLeft = tip.clone().addScaledVector(across, -tipWidth / 2).addScaledVector(outward, -foldDepth * tipWidth / rootWidth);
  const tipRight = tip.clone().addScaledVector(across, tipWidth / 2).addScaledVector(outward, -foldDepth * tipWidth / rootWidth);

  const positions = [
    ...root.toArray(), ...rootLeft.toArray(), ...tipLeft.toArray(),
    ...root.toArray(), ...tipLeft.toArray(), ...tip.toArray(),
    ...root.toArray(), ...tip.toArray(), ...tipRight.toArray(),
    ...root.toArray(), ...tipRight.toArray(), ...rootRight.toArray(),
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** Build a deterministic tuft; the default seed is 47. */
export function buildGrassTuft(options: GrassTuftOptions = {}): THREE.Group {
  const random = mulberry32(options.seed ?? DEFAULT_SEED);
  const group = new THREE.Group();
  group.name = 'grass-tuft';
  // ── Two values, and the darker one was lifted after the first frames ───────
  //
  // At 0x5a9b57 the three dark blades read as near-black at any distance in the
  // pre-bloom palette, so a tuft was two pale blades with a shadow behind them
  // rather than a clump. These are still both from the `outside` zone's family;
  // the point of having two at all is that a field of one green is a billiard
  // table.
  const dark = surface('organic', 0x63a95d);
  const light = surface('organic', 0x8ec46a);

  // Deliberately irregular azimuths. An even fifth of a circle reads as a fan,
  // which becomes painfully obvious when the same asset is instanced 1,600 times.
  const azimuths = [0.02, 1.57, 2.97, 4.69, 5.55] as const;
  // ── Shorter, and the reason is in a screenshot ─────────────────────────────
  //
  // These were 0.238 / 0.232 / 0.205 / 0.238 / 0.180 with a 29 degree lean and a
  // 20 mm root, and the first frames of the field showed what that is: **five
  // long thin splayed spikes**, which at 1,600 instances read as scattered wire
  // or as spider legs, and never once as turf. Nothing was wrong with any single
  // blade; the *proportions* were a reed and not a grass.
  //
  // Three numbers moved together, because changing one alone does not fix a
  // proportion: about a quarter shorter, nearly twice as wide at the root, and a
  // lean of 9–15 degrees instead of 29. What that buys is a clump with a legible
  // mass — and it costs nothing, because it is the same twenty triangles.
  const heights = [0.175, 0.170, 0.150, 0.175, 0.132] as const;
  for (let index = 0; index < azimuths.length; index += 1) {
    const baseAzimuth = azimuths[index];
    if (baseAzimuth === undefined) throw new Error('grass-tuft: missing blade azimuth');
    const bladeHeight = heights[index];
    if (bladeHeight === undefined) throw new Error('grass-tuft: missing blade height');
    const azimuth = baseAzimuth + (random() - 0.5) * 0.04;
    // Roots gathered, so the five blades read as one plant rather than five.
    const radius = 0.026 + random() * 0.006;
    const rootX = Math.cos(azimuth) * radius;
    const rootZ = Math.sin(azimuth) * radius;
    const height = bladeHeight + (random() - 0.5) * 0.004;
    const lean = 0.16 + random() * 0.10;
    const blade = new THREE.Mesh(
      bladeGeometry(rootX, rootZ, azimuth, height, 0.036, 0.011, lean),
      index < 3 ? dark : light,
    );
    blade.name = `blade-${index + 1}`;
    blade.castShadow = false;
    blade.receiveShadow = true;
    group.add(blade);
  }

  // Random roots and leans do not average to an honest placement origin. Centre
  // the measured mesh, rather than trusting one seed to get lucky at the gate.
  const bounds = new THREE.Box3().setFromObject(group);
  group.position.set(
    -(bounds.min.x + bounds.max.x) / 2,
    0,
    -(bounds.min.z + bounds.max.z) / 2,
  );
  return group;
}

export const grassTuftAsset: AssetDefinition = {
  id: 'grass-tuft',
  label: 'Grass tuft',
  description: 'Five folded, dawn-varied grass blades for the wet field.',
  /**
   * MEASURED, not intended. 0.118 × 0.175 × 0.132.
   *
   * It was `0.34 × 0.24 × 0.37` — the number from the brief — and the asset
   * contract went red at a 0.35 ratio the moment the blade proportions were
   * re-cut (a quarter shorter, gathered roots, a 9° lean instead of 29°). That
   * is the gate doing its job: **a declared footprint frames the acceptance
   * shots and every instrument that measures this asset**, so a stale one is a
   * lie about the mesh rather than a rounding.
   *
   * The tuft is deliberately small. A 0.12 m clump against a pig 0.32 m tall is
   * grass she can push through, and the field's read comes from 1,600 of them
   * rather than from any one being large.
   */
  footprint: { width: 0.118, height: 0.175, length: 0.132 },
  create: (): THREE.Group => buildGrassTuft(),
};
